import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { getApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { copyTextToClipboard } from "@/lib/clipboard";
import { getMarketplaces } from "@/lib/marketplaces";
import { formatUsedPrice } from "@/lib/price";
import { ArrowLeft, Bookmark, Copy, Loader2, Share2, Mail, CheckCircle2 } from "lucide-react";
import AdBanner from "@/components/AdBanner";
import ResultsHeader from "@/components/results/ResultsHeader";
import StrategyCard from "@/components/results/StrategyCard";
import ListingGenerator from "@/components/results/ListingGenerator";
import BundleAnalysis from "@/components/results/BundleAnalysis";

const Results = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [currentListing, setCurrentListing] = useState(null);
  const [email, setEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("rv_last_result");
    if (!raw) { navigate("/valutazione"); return; }
    const parsed = JSON.parse(raw);
    setPayload(parsed);
    // Auto-select the recommended strategy so the listing text is ready on open
    setSelectedGoal(parsed.input?.urgency || "balanced");
  }, [navigate]);

  useEffect(() => {
    if (user?.email) setEmail((e) => e || user.email);
  }, [user]);

  if (!payload) return null;
  const { input, result, photo_previews } = payload;
  const recommended = input.urgency || "balanced";
  const selectedStrategy = result.strategies.find((s) => s.goal === selectedGoal) || null;
  const categoryMarketplaces = getMarketplaces(input.category);
  const productName = `${input.brand || ""} ${input.model || ""}`.trim() || (input.category || "").replace(/_/g, " ");
  const chosenPrice = formatUsedPrice(selectedStrategy?.price ?? result.estimated_market_value);

  const handleSave = async () => {
    if (!user) { navigate("/login"); return; }
    setSaving(true);
    try {
      const res = await api.post("/valuations", { input, result });
      setSavedId(res.data.id);
      setSaved(true);
      toast.success(t.results.saved);
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    if (!savedId) return;
    setSharing(true);
    try {
      const res = await api.post(`/valuations/${savedId}/share`);
      const url = `${window.location.origin}/condivisa/${res.data.share_token}`;
      setShareUrl(url);
      const ok = await copyTextToClipboard(url);
      toast.success(ok ? t.results.share_copied : t.results.share_ready);
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setSharing(false);
    }
  };

  const handleSendEmail = async () => {
    const em = email.trim();
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { toast.error(t.results.email_invalid); return; }
    setSendingEmail(true);
    try {
      await api.post("/email/valuation", {
        recipient_email: em,
        product_name: productName,
        price: chosenPrice,
        estimated_value: formatUsedPrice(result.estimated_market_value),
        listing_title: currentListing?.title || null,
        listing_body: currentListing?.body || null,
        language: input.language || "it",
      });
      setEmailSent(true);
      toast.success(t.results.email_sent);
    } catch (err) {
      setEmailSent(false);
      toast.error(getApiError(err));
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <main data-testid="results-page" className="bg-white pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <Link to="/valutazione" className="inline-flex items-center gap-2 font-mono text-xs font-bold tracking-widest text-[#4A4A4A] hover:text-black">
          <ArrowLeft size={14} /> {t.common.back}
        </Link>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <ResultsHeader result={result} input={input} photoPreviews={photo_previews} />

          <div className="lg:col-span-12 -mb-2">
            <span data-testid="select-price-prompt" className="overline text-[#002FA7]">↓ {t.results.select_prompt}</span>
          </div>

          {result.strategies.map((s, i) => (
            <StrategyCard
              key={s.goal}
              strategy={s}
              index={i}
              recommended={recommended}
              selected={selectedGoal === s.goal}
              onSelect={() => setSelectedGoal(s.goal)}
              marketplaces={categoryMarketplaces}
            />
          ))}

          {input.category === "electronics_laptop" && input.pc_component_type === "desktop" && (
            <BundleAnalysis value={result.estimated_market_value} />
          )}

          <ListingGenerator input={input} strategy={selectedStrategy} onListing={setCurrentListing} marketplaces={categoryMarketplaces} />

          {/* EMAIL THE ESTIMATE */}
          <div data-testid="email-block" className="lg:col-span-12 border border-black p-5 sm:p-6 bg-[#002FA7]/[0.04]">
            <div className="flex items-center gap-2 text-[#002FA7]">
              <Mail size={16} />
              <span className="overline">{t.results.email_title}</span>
            </div>
            <p className="text-sm text-[#4A4A4A] mt-2">{t.results.email_hint}</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailSent(false); }}
                placeholder={t.results.email_placeholder}
                className="input-rv flex-1 text-gray-900"
              />
              <button
                data-testid="email-send-btn"
                onClick={handleSendEmail}
                disabled={sendingEmail || emailSent}
                className="btn-primary justify-center sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sendingEmail ? <Loader2 size={18} className="animate-spin" /> : (emailSent ? <CheckCircle2 size={18} /> : <Mail size={18} />)}
                {sendingEmail ? t.results.email_sending : (emailSent ? t.results.email_sent : t.results.email_send)}
              </button>
            </div>
            {emailSent && (
              <div data-testid="email-success" className="mt-3 flex items-center gap-2 text-sm font-bold text-[#137333]">
                <CheckCircle2 size={16} /> {t.results.email_sent}
              </div>
            )}
          </div>

          <div className="lg:col-span-12">
            <AdBanner slot="results" />
          </div>

          {/* ACTIONS */}
          <div className="lg:col-span-12 flex flex-wrap items-center justify-between gap-4 mt-4">
            <Link to="/valutazione" data-testid="results-new" className="btn-outline">{t.results.new_valuation}</Link>
            <div className="flex flex-wrap items-center gap-3">
              {saved && savedId && (
                <button data-testid="results-share" onClick={handleShare} disabled={sharing} className="btn-outline">
                  {sharing ? <Loader2 size={18} className="animate-spin" /> : <Share2 size={18} />} {t.results.share}
                </button>
              )}
              {user ? (
                <button data-testid="results-save" onClick={handleSave} disabled={saved || saving} className="btn-primary">
                  <Bookmark size={18} /> {saved ? t.results.saved : (saving ? "..." : t.results.save)}
                </button>
              ) : (
                <Link to="/login" data-testid="results-login-to-save" className="btn-primary">
                  <Bookmark size={18} /> {t.results.login_to_save}
                </Link>
              )}
            </div>
          </div>

          {shareUrl && (
            <div data-testid="share-url-box" className="lg:col-span-12 border border-black bg-[#FFCC00]/15 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="min-w-0">
                <span className="overline text-[9px]">{t.results.share_label}</span>
                <div data-testid="share-url" className="font-mono text-xs break-all mt-1">{shareUrl}</div>
              </div>
              <button
                data-testid="share-copy-btn"
                onClick={async () => { const ok = await copyTextToClipboard(shareUrl); toast.success(ok ? t.results.share_copied : t.results.share_ready); }}
                className="flex-shrink-0 inline-flex items-center gap-2 font-mono text-xs font-bold tracking-widest uppercase bg-black text-white px-3 py-2 hover:bg-[#002FA7] transition-colors"
              >
                <Copy size={12} /> {t.results.listing_copy}
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default Results;
